// WU-2.2 — /admin/queue-instructions list page.
//
// Lists standing instructions (active + cleared) for the tenant. A
// supervisor can set a new free-scope instruction (InstructionDialog) or
// clear an active one. Structural sibling of QueueBlocksListPage.
import { useMemo, useState } from "react";
import { AdminListHeader } from "@/components/admin/AdminListHeader";
import { showApiError } from "@/lib/error-toast";
import { formatTimestamp } from "@/lib/formatters";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { useQueueInstructions } from "../hooks/useQueueInstructions";
import { queueInstructionsApi } from "../api/client";
import { InstructionDialog } from "../components/InstructionDialog";
import styles from "../queue-instructions.module.css";

export function QueueInstructionsListPage() {
  const { instructions, loading, refetch } = useQueueInstructions();
  const { items: jobTypes } = useJobTypes();
  const [showDialog, setShowDialog] = useState(false);

  // Resolve scope → readable label: null = step-wide ("All job types"),
  // otherwise the job-type name — or, per spec §8, "(deleted job type)" when
  // the id no longer resolves to a known job type (orphaned lane row).
  const scopeLabel = useMemo(() => {
    const map = new Map(jobTypes.map((jt) => [jt.id, jt.name]));
    return (jobTypeId: string | null) =>
      jobTypeId === null ? "All job types" : (map.get(jobTypeId) ?? "(deleted job type)");
  }, [jobTypes]);

  async function handleClear(id: string) {
    if (!window.confirm("Clear this instruction? Operators will stop seeing it.")) {
      return;
    }
    try {
      await queueInstructionsApi.delete(id);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  }

  return (
    <div className={styles.listPage}>
      <AdminListHeader
        title="Queue Instructions"
        count={instructions.length}
        primaryAction={{
          label: "Set instruction",
          onClick: () => setShowDialog(true),
        }}
      />

      {loading ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : instructions.length === 0 ? (
        <div className={styles.emptyState}>
          No queue instructions. Setting one shows standing guidance to
          operators on that queue.{" "}
          <button
            type="button"
            className={styles.primary}
            style={{ marginLeft: 12 }}
            onClick={() => setShowDialog(true)}
          >
            Set instruction
          </button>
        </div>
      ) : (
        <table className={styles.instructionsTable}>
          <thead>
            <tr>
              <th>Step</th>
              <th>Job Type</th>
              <th>Instruction</th>
              <th>Set By</th>
              <th>Set At</th>
              <th>Cleared At</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {instructions.map((i) => {
              const active = i.clearedAt === null;
              const author = i.createdByName ?? i.createdBy;
              return (
                <tr key={i.id}>
                  <td>
                    <code>{i.stepType}</code>
                  </td>
                  <td>{scopeLabel(i.jobTypeId)}</td>
                  <td className={styles.bodyCell} title={i.body}>
                    {i.body}
                  </td>
                  <td>{author}</td>
                  <td>{formatTimestamp(i.createdAt)}</td>
                  <td>{formatTimestamp(i.clearedAt)}</td>
                  <td>
                    {active ? (
                      <span className={styles.activeBadge}>● Active</span>
                    ) : (
                      <span className={styles.clearedBadge}>Cleared</span>
                    )}
                  </td>
                  <td>
                    {active && (
                      <div className={styles.rowActions}>
                        <button type="button" onClick={() => handleClear(i.id)}>
                          Clear
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showDialog && (
        <InstructionDialog
          jobTypes={jobTypes.map((jt) => ({ id: jt.id, name: jt.name }))}
          initialBody={null}
          existingId={null}
          onClose={() => setShowDialog(false)}
          onSaved={() => void refetch()}
        />
      )}
    </div>
  );
}

export default QueueInstructionsListPage;
